import productMapping from 'root/global_configs/product_mapping';
import formConstants from 'root/global_configs/formConstants.json';
import { progressIndicator } from 'root/main/components/progressIndicator';
import { handleZipCodeField } from 'root/helpers/formHelpers';

export default function AgnosticFormController(el) {
    this.form = el;

    this.isHiddenField = fields => {
        if (fields) {
            return Array.from(fields).every(field => field.type === 'hidden');
        } else {
            return false;
        }
    };

    this.contentIDHiddenFields =
        this.isHiddenField(this.form.content_post_id) && this.form.content_post_id;
    this.formID = this.isHiddenField(this.form.form_id) && this.form.form_id;
    this.productSelectField = this.form.querySelector('select[name=product]');
    this.agnosticBusinessTypeField = this.form.agnostic_business_type;
    this.roleSelectField = this.form.role;
    this.agnosticBusinessTypeFieldOptions = this.agnosticBusinessTypeField
        ? this.agnosticBusinessTypeField.options
        : [];
}

AgnosticFormController.prototype = {
    /**
     * Remove the hidden fields of name content_post_id (on submit) which don't match the selected product
     */
    removeNotMatchingContentIDHiddenField(productSelectedValue) {
        Array.from(this.contentIDHiddenFields)
            .filter(
                field =>
                    this.productSelectField &&
                    productSelectedValue !== field.dataset.productName.toLowerCase()
            )
            .forEach(field => field.remove());
    },
    /**
     * Store the hidden business type spans corresponding to the selected product in an array
     * @param productSelectedValue
     * @returns {unknown[]}
     */
    getMatchingBusinessTypeSpans(productSelectedValue) {
        const spanEls = this.agnosticBusinessTypeField
            ? this.agnosticBusinessTypeField.querySelectorAll('span')
            : [];
        return Array.from(spanEls).filter(
            spanEl =>
                spanEl.dataset.productName &&
                spanEl.dataset.productName.toLowerCase() === productSelectedValue
        );
    },
    /**
     * Store the business type options which are not corresponding to the selected product in an array
     * @param productSelectedValue
     * @returns {unknown[]}
     */
    getNotMatchingBusinessTypeOptions(productSelectedValue) {
        return (
            this.agnosticBusinessTypeFieldOptions.length > 0 &&
            Array.from(this.agnosticBusinessTypeFieldOptions).filter(
                option =>
                    option.dataset.productName &&
                    option.dataset.productName.toLowerCase() !== productSelectedValue
            )
        );
    },
    /**
     * Convert the option HTML tag to a span HTML tag in order to hide it
     * and copy the value and data attribute from the option tag to the newly created span tag
     * @param optionEl
     */
    convertOptionToSpanTag(optionEl) {
        const spanEl = document.createElement('span');
        spanEl.innerHTML = optionEl.innerHTML;
        spanEl.setAttribute('value', optionEl.value);
        spanEl.setAttribute('data-product-name', optionEl.dataset.productName);
        spanEl.style.display = 'none';
        optionEl.parentElement.replaceChild(spanEl, optionEl);
    },
    /**
     * Convert the span HTML tag to an option HTML tag in order to show it
     * and restore its value and data attribute
     * @param spanEl
     */
    convertSpanToOptionTag(spanEl) {
        const optionEl = document.createElement('option');
        optionEl.innerHTML = spanEl.innerHTML;
        optionEl.setAttribute('value', spanEl.getAttribute('value'));
        optionEl.setAttribute('data-product-name', spanEl.dataset.productName);
        spanEl.parentElement.replaceChild(optionEl, spanEl);
        optionEl.style.display = 'inline-block';
    },
    /**
     * Based on the selected product, show the options corresponding to this product,
     * and hide the options which are not
     * @param optionsToHide
     * @param spansToShow
     */
    alternateDisplayingBusinessTypeOptions(optionsToHide = [], spansToShow = []) {
        /**
         * On Safari hiding the select field option will not work,
         * as a workaround we're converting the option to a span HTML tag to hide it
         * and converting the span HTML tag back to an option HTML tag to show it
         */
        Array.isArray(optionsToHide) &&
            optionsToHide.length > 0 &&
            optionsToHide.forEach(option => this.convertOptionToSpanTag(option));

        Array.isArray(spansToShow) &&
            spansToShow.length > 0 &&
            spansToShow.forEach(spanEl => this.convertSpanToOptionTag(spanEl));
    },
    /* Show the role field when "golf" is selected */
    showGolfRole() {
        if (this.roleSelectField) {
            this.roleSelectField.parentElement.style.display = 'block';
            this.roleSelectField.options[0].value = '';
            this.roleSelectField.options[0].text = '';
            this.roleSelectField.options[0].selected = false;
            if (this.roleSelectField.closest('[data-formfield=ls-form-field]'))
                this.roleSelectField
                    .closest('[data-formfield=ls-form-field]')
                    .classList.remove('js-field-valid');
        }
    },
    /* Hide the role select field when a product other than golf was selected */
    // TODO: Refactor the default-role-field behaviour to toggle required on/off instead, then remove raiseLabel hack in formUI.js
    hideGolfRole() {
        if (this.roleSelectField) {
            this.roleSelectField.parentElement.style.display = 'none';
            this.roleSelectField.options[0].value = 'default-role-field';
            this.roleSelectField.options[0].text = 'default-role-field';
            this.roleSelectField.options[0].selected = true;
            this.roleSelectField.parentElement.classList.remove('js-field-invalid');
            this.roleSelectField.classList.remove('js-input-error');
        }
        this.resetBusinessTypeOnProductSelection();
    },
    /* revert changes when another product has been selected */
    showAgnosticBusinessTypeField() {
        if (this.agnosticBusinessTypeField) {
            this.agnosticBusinessTypeField.parentElement.style.display = 'block';
            if (this.agnosticBusinessTypeField.closest('[data-formfield=ls-form-field]'))
                this.agnosticBusinessTypeField
                    .closest('[data-formfield=ls-form-field]')
                    .classList.remove('js-field-valid');
        }
    },
    hideAgnosticBusinessTypeField() {
        if (this.agnosticBusinessTypeFieldOptions.length > 0) {
            this.agnosticBusinessTypeField.parentElement.style.display = 'none';
            /* change the first option form having an empty space as a value to golf as a value */
            this.agnosticBusinessTypeFieldOptions[0].value = 'golf';
            /* This is important to make the form submit correctly on iOS devices */
            this.agnosticBusinessTypeFieldOptions[0].text = 'golf';
            /* set the product data attribute to be converted to span and gets hidden when another product is selected */
            this.agnosticBusinessTypeFieldOptions[0].setAttribute(
                'data-product-name',
                'golf'
            );
            /* force select the first option which will make the field valid */
            this.agnosticBusinessTypeFieldOptions[0].selected = true;
        }
    },
    /**
     * When golf is selected the business type field should be hidden
     * set the first option of the business type field to 'golf' and force select
     * this will make the field valid, and the form can be submitted as the submit button will be enabled
     * @param productSelectedValue
     */
    showCorrectSelectElement(productSelectedValue) {
        if (productSelectedValue === 'empty') {
            this.hideAgnosticBusinessTypeField();
            this.hideGolfRole();
        } else if (productSelectedValue === 'golf') {
            this.hideAgnosticBusinessTypeField();
            this.showGolfRole();
        } else {
            this.showAgnosticBusinessTypeField();
            this.hideGolfRole();
        }
    },
    /**
     * reset the business type everytime a new product is selected
     */
    resetBusinessTypeOnProductSelection() {
        /**
         *  in case another product has been selected after golf was previously selected,
         *  we want to make sure to have an empty option selected
         */
        if (this.agnosticBusinessTypeFieldOptions.length > 0) {
            if (this.agnosticBusinessTypeFieldOptions[0].text !== '') {
                const emptyOptionEl = document.createElement('option');
                if (this.agnosticBusinessTypeField)
                    this.agnosticBusinessTypeField.add(emptyOptionEl, '');
                Array.from(this.agnosticBusinessTypeFieldOptions).forEach(option => {
                    if (option.text === '') {
                        option.selected = true;
                    }
                });
            } else {
                /* reset the business types field to have an empty option selected */
                this.agnosticBusinessTypeFieldOptions[0].value = '';
                this.agnosticBusinessTypeFieldOptions[0].text = '';
                this.agnosticBusinessTypeFieldOptions[0].selected = true;
            }
        }
    },
    /**
     * Remove agnostic_ from business_type hidden field in order to be received correctly by external service
     */
    restoreBusinessTypeName() {
        if (this.agnosticBusinessTypeField) {
            this.agnosticBusinessTypeField.name =
                this.agnosticBusinessTypeField.name.replace(/agnostic_/g, '');
        }
    },
    /**
     * Return the selected product
     */
    getProductSelectedValue() {
        if (this.productSelectField?.value) {
            return productMapping[this.productSelectField.value] ?? '';
        } else {
            return 'empty';
        }
    },
    /**
     * Aggregate the functionality that will be executed when the product is selected (on change event)
     */
    eventHandlerOnChange() {
        const productSelectedValue = this.getProductSelectedValue();
        if (productSelectedValue) {
            this.showCorrectSelectElement(productSelectedValue);
            this.alternateDisplayingBusinessTypeOptions(
                this.getNotMatchingBusinessTypeOptions(productSelectedValue),
                this.getMatchingBusinessTypeSpans(productSelectedValue)
            );
            handleZipCodeField(this.form, productSelectedValue);
        }
        const timelineContainer = this.form.querySelector(
            '[data-leadform-step=progress-indicator]'
        );
        if (timelineContainer)
            progressIndicator(timelineContainer, 0).syncProgressIndicatorToStep();
    },
    /**
     * Aggregate the functionality that will be executed when the form is submitted (on submit event)
     */
    eventHandlerOnSubmit() {
        const productSelectedValue = this.getProductSelectedValue();
        productSelectedValue &&
            this.removeNotMatchingContentIDHiddenField(productSelectedValue);
        this.restoreBusinessTypeName();
    },
    /**
     * Catch to make sure that the minHeight of the form always reflects the form elements visible on page load, in case agnosticFormController.js runs after formUI.js and has hidden some elements
     */
    updateFormHeight() {
        if (!this.form.style.minHeight) {
            return;
        }

        const formHeight = isNaN(parseInt(this.form.clientHeight))
            ? 'auto'
            : formConstants.errorMessageDefaultHeight + parseInt(this.form.clientHeight);
        this.form.style.minHeight =
            formHeight && formHeight !== 'auto' ? `${formHeight}px` : 'auto';
    },
    /**
     * the init function is invoked in the lightboxFormInit in case the form has been loaded in a lightbox
     * and on DOMContentLoaded in case the form has been loaded on a page
     */
    init() {
        this.productSelectField?.addEventListener(
            'change',
            this.eventHandlerOnChange.bind(this)
        );
        this.form.addEventListener('submit', this.eventHandlerOnSubmit.bind(this));
        this.getProductSelectedValue() &&
            this.showCorrectSelectElement(this.getProductSelectedValue());
        handleZipCodeField(this.form);
        this.updateFormHeight();
    },
};
